import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, ArrowsClockwise, Warning, CheckCircle, ArrowsMerge } from '@phosphor-icons/react';
import { paymentsApi, formatApiError } from '../services/api';

export const DuplicateWeeksModal = ({ onClose, onMerged, formatCurrency, formatDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null); // key | 'all'

  const load = async () => {
    try {
      setLoading(true);
      const res = await paymentsApi.auditDuplicateWeeks();
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mergeOne = async (item) => {
    const key = `${item.contract_id}-${item.week_number}`;
    if (!window.confirm(
      `¿Unificar los ${item.docs_count} pagos de ${item.client_name} · Semana ${item.week_number} en un solo registro?\n\n` +
      `Total cobrado: ${formatCurrency(item.total_paid)} (no cambia).\n` +
      `Se conservará el historial de abonos dentro del pago.`
    )) return;
    try {
      setMerging(key);
      await paymentsApi.mergeDuplicateWeeks({ contract_id: item.contract_id, week_number: item.week_number });
      toast.success(`Semana ${item.week_number} de ${item.client_name} unificada`);
      await load();
      onMerged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMerging(null);
    }
  };

  const mergeAll = async () => {
    if (!data?.items?.length) return;
    if (!window.confirm(
      `¿Unificar TODAS las semanas repetidas (${data.total_groups} semanas, ${data.total_extra_docs} registros extra)?\n\n` +
      `Los totales cobrados y saldos de contratos NO cambian; solo se consolidan los registros.`
    )) return;
    try {
      setMerging('all');
      const res = await paymentsApi.mergeDuplicateWeeks({ all: true });
      toast.success(`${res.data.merged_groups} semanas unificadas`);
      await load();
      onMerged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMerging(null);
    }
  };

  return (
    <div className="modal-overlay" data-testid="duplicate-weeks-modal">
      <div className="modal-content animate-fade-in" style={{ maxWidth: '1024px' }}>
        <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-bold flex items-center gap-2">
              <Warning size={22} className="text-amber-500" />
              Validación: semanas con pagos repetidos
            </h2>
            <p className="text-sm text-[#64748B] mt-1">
              Regla: un solo registro de pago por semana. Los abonos de una misma semana se unifican en un registro con historial.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-secondary flex items-center gap-1 text-sm" disabled={loading} data-testid="dup-weeks-refresh">
              <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} /> Revalidar
            </button>
            <button onClick={onClose} className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded" data-testid="dup-weeks-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading && !data ? (
            <div className="flex justify-center py-10"><div className="spinner" /></div>
          ) : !data || data.total_groups === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="dup-weeks-empty">
              <CheckCircle size={48} className="text-emerald-500 mb-3" weight="fill" />
              <p className="text-lg font-semibold text-[#0F172A]">Sin semanas repetidas</p>
              <p className="text-sm text-[#64748B]">Todos los contratos tienen un solo registro de pago por semana.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-900" data-testid="dup-weeks-summary">
                  <span className="font-bold">{data.total_groups}</span> semana{data.total_groups !== 1 ? 's' : ''} con pagos repetidos ·{' '}
                  <span className="font-bold">{data.total_extra_docs}</span> registro{data.total_extra_docs !== 1 ? 's' : ''} extra
                </p>
                <button onClick={mergeAll} disabled={merging !== null} className="btn-primary flex items-center gap-2 text-sm" data-testid="dup-weeks-merge-all">
                  <ArrowsMerge size={16} /> {merging === 'all' ? 'Unificando…' : 'Unificar todas'}
                </button>
              </div>

              <div className="space-y-3">
                {data.items.map((it) => {
                  const key = `${it.contract_id}-${it.week_number}`;
                  return (
                    <div key={key} className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`dup-group-${key}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 bg-[#F8FAFC]">
                        <div>
                          <p className="font-semibold text-[#0F172A]">
                            {it.client_name} <span className="text-[#94A3B8] font-normal">· {it.vehicle_label} ({it.vehicle_plates})</span>
                          </p>
                          <p className="text-xs text-[#64748B]">
                            Semana {it.week_number} · Vence {formatDate(it.due_date)} · Renta {formatCurrency(it.expected_amount)} · Debe total {formatCurrency(it.total_owed)}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-xs text-[#64748B]">{it.docs_count} pagos · cobrado</p>
                            <p className="font-bold text-[#0F172A]">{formatCurrency(it.total_paid)}</p>
                            {it.over_collected > 0 && (
                              <p className="text-[11px] text-red-600 font-semibold">Excedente {formatCurrency(it.over_collected)}</p>
                            )}
                          </div>
                          <button onClick={() => mergeOne(it)} disabled={merging !== null} className="btn-secondary text-xs flex items-center gap-1" data-testid={`dup-merge-${key}`}>
                            <ArrowsMerge size={14} /> {merging === key ? 'Unificando…' : 'Unificar'}
                          </button>
                        </div>
                      </div>
                      <table className="data-table text-sm">
                        <thead>
                          <tr><th>Fecha</th><th className="text-right">Monto</th><th className="text-right">Mora</th><th className="text-right">Accesorio</th><th>Método</th><th>Notas</th></tr>
                        </thead>
                        <tbody>
                          {it.payments.map((p) => (
                            <tr key={p.id}>
                              <td>{formatDate(p.payment_date)}</td>
                              <td className="text-right font-semibold text-emerald-600">{formatCurrency(p.amount)}</td>
                              <td className="text-right">{formatCurrency(p.penalty_amount)}</td>
                              <td className="text-right">{formatCurrency(p.accessory_amount)}</td>
                              <td className="capitalize">{p.payment_method}</td>
                              <td className="text-[#64748B] truncate max-w-[200px]">{p.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
